/**
 * 管理者用レイアウト
 * 
 * @file AdminLayout.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * 管理者向け管理画面の共通レイアウト。
 * ヘッダー、ユーザーメニュー、サイドバーナビゲーションを提供。
 */

import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import MockBanner from '@shared/components/MockBanner';
import { useLanguage, I18nLoadingScreen } from '@shared/contexts/LanguageContext';
import { useAuth, useAdminRole } from '@shared/contexts/useAuth';
import { UserRole } from '@shared/types';
import { ensureInitialized } from '@shared/i18n';

interface AdminLayoutProps {
  children?: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const adminRole = useAdminRole();
  const isSuperAdmin = adminRole === UserRole.SUPER_ADMIN;
  const canViewAdministratorSettings = adminRole === UserRole.SUPER_ADMIN || adminRole === UserRole.ADMIN;
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [isInitialized, setIsInitialized] = useState(false);

  // i18nextの初期化
  useEffect(() => {
    ensureInitialized().then(() => {
      setIsInitialized(true);
    });
  }, []);

  // メニュー外クリック時の自動クローズ（ユーザーメニューとモバイルサイドバー）
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // ユーザーメニューのクローズ処理
      const userMenu = document.getElementById('userMenu');
      const adminUser = target.closest('.admin-user');
      
      if (showUserMenu && userMenu && !adminUser) {
        setShowUserMenu(false);
      }
      
      // モバイルサイドバーのクローズ処理
      if (isMobile) {
        const sidebar = target.closest('.admin-sidebar');
        const toggleButton = target.closest('.sidebar-toggle');
        
        if (!sidebar && !toggleButton) {
          handleSidebarClose();
        }
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [showUserMenu, isMobile]);

  // 画面サイズ変更時のレスポンシブ対応
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        handleSidebarClose();
      }
      else {
        document.body.classList.remove('sidebar-collapsed');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // ページ遷移時にスクロールをトップに戻す
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleUserMenuClose = () => {
    setShowUserMenu(false);
  };

  const handleLogout = async () => {
    try {
      handleUserMenuClose();
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
    // エラー発生時もログイン画面へ遷移
    navigate('/login');
  };

  // サイドバー切り替え機能
  const handleSidebarToggle = () => {
    document.body.classList.toggle('sidebar-collapsed');
  };

  const handleSidebarClose = () => {
    document.body.classList.add('sidebar-collapsed');
  };

  // i18next初期化完了まで待機
  if (!isInitialized) {
    return <I18nLoadingScreen />;
  }

  return (
    <div className="admin-layout">
      {/* モックバナー */}
      <MockBanner />

      {/* 共通ヘッダー */}
      <header className="admin-header">
        <div className="admin-header-container">

          {/* ハンバーガーメニュー */}
          <div className="sidebar-toggle" onClick={handleSidebarToggle}>
            <i className="material-icons">menu</i>
          </div>

          {/* ヘッダーロゴ */}
          <div className="admin-logo">
            <Link to="/admin" className="admin-logo-text">
              <div className="admin-site-name">{t('layout.admin.header.logo')}</div>
              <div className="admin-system-name">{t('layout.admin.header.system_name')}</div>
            </Link>
          </div>

          {/* ヘッダーナビゲーション */}
          <div className="nav-links">
            <Link to="/admin/help" className="help-link">
              <i className="material-icons">help_outline</i>
              <span>{t('layout.admin.header.help')}</span>
            </Link>
            <div className="admin-user" onClick={handleUserMenuToggle}>
              <div className="admin-user-avatar">A</div>
              <div className="admin-user-name">{user?.firstName}</div>
              <i className="material-icons">arrow_drop_down</i>

              {/* ユーザーメニュー */}
              <div className={`user-menu ${showUserMenu ? 'show' : ''}`} id="userMenu" style={{ display: showUserMenu ? 'block' : 'none' }}>
                {/* ユーザー情報ヘッダー */}
                <div className="user-menu-header">
                  <div className="user-menu-avatar">A</div>
                  <div className="user-menu-info">
                    <div className="user-menu-name">{user?.firstName}</div>
                    <div className="user-menu-email">{user?.email}</div>
                  </div>
                </div>

                <hr className="user-menu-divider" />

                {/* メニュー項目 */}
                <Link to="/admin/settings/languages" className={`user-menu-item ${location.pathname.startsWith('/admin/settings') ? 'active' : ''}`}>
                  <i className="material-icons">store</i>
                  <span>{t('layout.admin.user_menu.system_settings')}</span>
                </Link>
                <Link to="/admin/static-contents" className={`user-menu-item ${location.pathname.startsWith('/admin/static-contents') ? 'active' : ''}`}>
                  <i className="material-icons">article</i>
                  <span>{t('layout.admin.user_menu.static_contents')}</span>
                </Link>
                <Link to="/admin/faq-settings" className={`user-menu-item ${location.pathname.startsWith('/admin/faq-settings') ? 'active' : ''}`}>
                  <i className="material-icons">help</i>
                  <span>{t('layout.admin.user_menu.help_faq')}</span>
                </Link>
                <Link to="/admin/shipping/carriers" className={`user-menu-item ${location.pathname.startsWith('/admin/shipping') ? 'active' : ''}`}>
                  <i className="material-icons">local_shipping</i>
                  <span>{t('layout.admin.user_menu.shipping')}</span>
                </Link>
                {canViewAdministratorSettings && (
                  <Link to="/admin/administrator/list" className={`user-menu-item ${location.pathname.startsWith('/admin/administrator') ? 'active' : ''}`}>
                    <i className="material-icons">store</i>
                    <span>{t('layout.admin.user_menu.administrator_settings')}</span>
                  </Link>
                )}
                <Link to="/admin/notifications" className={`user-menu-item ${location.pathname.startsWith('/admin/notifications') ? 'active' : ''}`}>
                  <i className="material-icons">notifications</i>
                  <span>{t('layout.admin.user_menu.notification_settings')}</span>
                </Link>
                <Link to="/admin/account" className={`user-menu-item ${location.pathname.startsWith('/admin/account') ? 'active' : ''}`}>
                  <i className="material-icons">account_circle</i>
                  <span>{t('layout.admin.user_menu.account')}</span>
                </Link>

                <hr className="user-menu-divider" />

                <div className="user-menu-item logout" onClick={handleLogout}>
                  <i className="material-icons">logout</i>
                  <span>{t('layout.admin.user_menu.logout')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 共通サイドバー */}
      <aside className="admin-sidebar">
        <div className="nav-group">
          <div className="nav-group-title">{t('layout.admin.sidebar.main_menu.title')}</div>
          <Link to="/admin" className={`nav-item ${location.pathname === '/admin' ? 'active' : ''}`}>
            <i className="material-icons">dashboard</i>
            <span className="nav-label">{t('layout.admin.sidebar.main_menu.dashboard')}</span>
          </Link>
          <Link to="/admin/products/list" className={`nav-item ${location.pathname.startsWith('/admin/products') ? 'active' : ''}`}>
            <i className="material-icons">shopping_bag</i>
            <span className="nav-label">{t('layout.admin.sidebar.main_menu.products')}</span>
          </Link>
          <Link to="/admin/orders" className={`nav-item ${location.pathname.startsWith('/admin/orders') ? 'active' : ''}`}>
            <i className="material-icons">receipt_long</i>
            <span className="nav-label">{t('layout.admin.sidebar.main_menu.orders')}</span>
          </Link>
          <Link to="/admin/inventory" className={`nav-item ${location.pathname.startsWith('/admin/inventory') ? 'active' : ''}`}>
            <i className="material-icons">inventory</i>
            <span className="nav-label">{t('layout.admin.sidebar.main_menu.inventory')}</span>
          </Link>
          <Link to="/admin/buyers" className={`nav-item ${location.pathname.startsWith('/admin/buyers') ? 'active' : ''}`}>
            <i className="material-icons">people</i>
            <span className="nav-label">{t('layout.admin.sidebar.main_menu.buyers')}</span>
          </Link>
            <Link to="/admin/logistics/incoming" className={`nav-item ${location.pathname.startsWith('/admin/logistics') ? 'active' : ''}`}>
            <i className="material-icons">local_shipping</i>
            <span className="nav-label">{t('layout.admin.sidebar.main_menu.logistics')}</span>
          </Link>
          {isSuperAdmin && (
          <Link to="/admin/external-sites" className={`nav-item ${location.pathname.startsWith('/admin/external-sites') ? 'active' : ''}`}>
            <i className="material-icons">public</i>
            <span className="nav-label">{t('layout.admin.sidebar.main_menu.external_sites')}</span>
          </Link>
          )}
          {isSuperAdmin && (
          <Link to="/admin/proxy-purchases" className={`nav-item ${location.pathname.startsWith('/admin/proxy-purchases') ? 'active' : ''}`}>
            <i className="material-icons">shopping_cart</i>
            <span className="nav-label">{t('layout.admin.sidebar.main_menu.proxy_purchases')}</span>
          </Link>
          )}
          <Link to="/admin/marketing/campaigns" className={`nav-item ${location.pathname.startsWith('/admin/marketing') ? 'active' : ''}`}>
            <i className="material-icons">campaign</i>
            <span className="nav-label">{t('layout.admin.sidebar.main_menu.marketing')}</span>
          </Link>
        </div>

        <div className="nav-group">
          <div className="nav-group-title">{t('layout.admin.sidebar.analytics.title')}</div>
          <Link to="/admin/analytics" className={`nav-item ${location.pathname.startsWith('/admin/analytics') ? 'active' : ''}`}>
            <i className="material-icons">bar_chart</i>
            <span className="nav-label">{t('layout.admin.sidebar.analytics.sales')}</span>
          </Link>
          <Link to="/admin/system-info" className={`nav-item ${location.pathname.startsWith('/admin/system-info') ? 'active' : ''}`}>
            <i className="material-icons">campaign</i>
            <span className="nav-label">{t('layout.admin.sidebar.analytics.system_info')}</span>
          </Link>
        </div>

        <div className="nav-group">
          <div className="nav-group-title">{t('layout.admin.sidebar.seller_management.title')}</div>
          <Link to="/admin/sellers/list" className={`nav-item ${location.pathname.startsWith('/admin/sellers') ? 'active' : ''}`}>
            <i className="material-icons">store</i>
            <span className="nav-label">{t('layout.admin.sidebar.seller_management.sellers')}</span>
          </Link>
          <Link to="/admin/payments/list" className={`nav-item ${location.pathname.startsWith('/admin/payments') ? 'active' : ''}`}>
            <i className="material-icons">payments</i>
            <span className="nav-label">{t('layout.admin.sidebar.seller_management.payments')}</span>
          </Link>
        </div>

        <div className="nav-group">
          <div className="nav-group-title">{t('layout.admin.sidebar.support.title')}</div>
          <Link to="/admin/chat-support" className={`nav-item ${location.pathname.startsWith('/admin/chat-support') ? 'active' : ''}`}>
            <i className="material-icons">chat</i>
            <span className="nav-label">{t('layout.admin.sidebar.support.chat')}</span>
          </Link>
          <Link to="/admin/help" className={`nav-item ${location.pathname.startsWith('/admin/help') ? 'active' : ''}`}>
            <i className="material-icons">help</i>
            <span className="nav-label">{t('layout.admin.sidebar.support.help_faq')}</span>
          </Link>
        </div>
      </aside>

      {/* メインコンテンツ */}
      <main className="main-content">
        {children ? children : <Outlet />}
      </main>

      {/* 共通フッター */}
      <footer className="admin-footer">
        <div className="admin-footer-container">
          <div>{t('common.brand.copyright')}</div>
          <div>v{import.meta.env.VITE_APP_VERSION}</div>
        </div>
      </footer>
    </div>
  );
};

export default AdminLayout;
